import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { num } from '../../format';
import { ScaleIcon } from '../../components/ui/icons';
import { useScanTarget } from '../../platform/scanning';
import { ComponentPicker } from '../components/ComponentPicker';
import { getComponent, type ComponentBrief } from '../components/api';
import { LocationPicker } from '../locations/LocationPicker';
import { resolveScan } from '../scan/api';
import {
  cancelStocktake,
  countStocktake,
  getStocktake,
  listStocktakes,
  measureStocktake,
  observeStocktakeSerial,
  openStocktake,
  postStocktake,
  setStocktakeLocation,
  type Stocktake,
  type StocktakeLine,
} from '../stock/api';

export function StocktakesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const sessions = useQuery({ queryKey: ['stocktakes'], queryFn: listStocktakes });

  return (
    <div className={`stocktakes${id ? ' is-detail' : ''}`}>
      <aside className="stocktakes__list">
        <header>
          <h2>{t('stocktakes.title')}</h2>
          <button type="button" onClick={() => setCreating((value) => !value)}>+</button>
        </header>
        {creating && <CreateStocktake onCreated={(next) => { setCreating(false); navigate(`/stocktakes/${next.id}`); }} />}
        {(sessions.data ?? []).map((session) => (
          <button
            type="button"
            key={session.id}
            className={session.id === id ? 'stocktakes__item is-active' : 'stocktakes__item'}
            onClick={() => navigate(`/stocktakes/${session.id}`)}
          >
            <strong>{session.code}</strong>
            <span>{session.note || t('stocktakes.scope', { n: session.location_count })}</span>
            <small>{t(`stocktakes.status.${session.status}`)}</small>
          </button>
        ))}
      </aside>
      <main className="stocktakes__detail">
        {id ? <StocktakeDetail id={id} /> : <p className="panel__hint">{t('stocktakes.pick')}</p>}
      </main>
    </div>
  );
}

function CreateStocktake({ onCreated }: { onCreated: (value: Stocktake) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [location, setLocation] = useState<string | null>(null);
  const [descendants, setDescendants] = useState(true);
  const [note, setNote] = useState('');
  const create = useMutation({
    mutationFn: openStocktake,
    onSuccess: (value) => {
      void queryClient.invalidateQueries({ queryKey: ['stocktakes'] });
      onCreated(value);
    },
  });
  return (
    <form className="stocktakes__create" onSubmit={(event) => { event.preventDefault(); create.mutate({ location_id: location, include_descendants: location ? descendants : true, note: note || null }); }}>
      <LocationPicker label={t('stocktakes.location')} value={location} onChange={setLocation} allowOutside />
      <label className="check"><input type="checkbox" checked={descendants} disabled={!location} onChange={(event) => setDescendants(event.target.checked)} />{t('stocktakes.descendants')}</label>
      <input value={note} onChange={(event) => setNote(event.target.value)} placeholder={t('stocktakes.note')} />
      <p className="hint">{location ? t(descendants ? 'stocktakes.branchHint' : 'stocktakes.placeHint') : t('stocktakes.allHint')}</p>
      <button type="submit" disabled={create.isPending}>{t('stocktakes.create')}</button>
    </form>
  );
}

function StocktakeDetail({ id }: { id: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [locationId, setLocationId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'different'>('pending');
  const [component, setComponent] = useState<ComponentBrief | null>(null);
  const [quantity, setQuantity] = useState('');
  const [measuring, setMeasuring] = useState(false);
  const selectedComponent = useQuery({ queryKey: ['components', component?.id], queryFn: () => getComponent(component!.id), enabled: Boolean(component) });
  useScanTarget((raw) => {
    void resolveScan(raw).then(async (found) => {
      if (!found.resolved || found.match?.kind !== 'component') return;
      const picked = await getComponent(found.match.id);
      setComponent(picked);
      setQuantity(session?.lines.find((line) => line.component_id === picked.id && line.location_id === selectedLocation)?.counted_qty ?? '');
      setMeasuring(false);
    });
  }, true);
  const detail = useQuery({ queryKey: ['stocktakes', id], queryFn: () => getStocktake(id) });
  const session = detail.data;
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['stocktakes', id] });
    void queryClient.invalidateQueries({ queryKey: ['stocktakes'] });
  };
  const count = useMutation({ mutationFn: (input: { component_id: string; location_id: string; counted_qty: string }) => countStocktake(id, input), onSuccess: refresh });
  const finishPlace = useMutation({
    mutationFn: ({ place, complete }: { place: string; complete: boolean }) => setStocktakeLocation(id, place, complete),
    onSuccess: (_value, { place, complete }) => {
      refresh();
      if (complete) {
        const next = session?.locations.find((candidate) => candidate.location_id !== place && candidate.status === 'pending');
        if (next) setLocationId(next.location_id);
      }
    },
  });
  const post = useMutation({ mutationFn: () => postStocktake(id, session?.lines.some((line) => line.changed_during_count) ?? false), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: () => cancelStocktake(id), onSuccess: refresh });

  const selectedLocation =
    locationId ??
    session?.locations.find((place) => place.status === 'pending')?.location_id ??
    session?.locations[0]?.location_id ??
    null;
  const lines = useMemo(() => (session?.lines ?? []).filter((line) => {
    if (selectedLocation && line.location_id !== selectedLocation) return false;
    if (filter === 'pending') return line.counted_qty === null;
    if (filter === 'different') return line.counted_qty !== null && line.counted_qty !== line.expected_qty;
    return true;
  }), [session, selectedLocation, filter]);

  if (!session) return <p className="panel__hint">{t('common.loading')}</p>;
  const open = session.status === 'open';
  const counted = session.lines.filter((line) => line.counted_qty !== null).length;
  const currentPlace = session.locations.find((place) => place.location_id === selectedLocation);
  const selectPlace = (place: Stocktake['locations'][number]) => {
    setLocationId(place.location_id);
    if (place.status === 'completed') setFilter('all');
  };

  return (
    <section className="stocktake">
      <header className="stocktake__head">
        <button type="button" className="stocktake__back" onClick={() => navigate('/stocktakes')}>←</button>
        <div><h2>{session.code}</h2><p>{session.note}</p></div>
        <div className={`stocktake__status stocktake__status--${session.status}`}><small>{t('stocktakes.statusLabel')}</small><strong>{t(`stocktakes.status.${session.status}`)}</strong></div>
      </header>
      <p>{t('stocktakes.progress', { counted, total: session.lines.length, places: session.locations.filter((place) => place.status === 'completed').length, placeTotal: session.locations.length })}</p>
      <section className="stocktake__places" aria-labelledby="stocktake-places-title">
        <header>
          <div>
            <h3 id="stocktake-places-title">{t('stocktakes.placesTitle', { n: session.locations.length })}</h3>
            <p>{t('stocktakes.placesHint')}</p>
          </div>
          <span>{t('stocktakes.placesLeft', { n: session.locations.filter((place) => place.status === 'pending').length })}</span>
        </header>
        <div className="stocktake__place-list">
          {session.locations.map((place) => {
            const placeLines = session.lines.filter((line) => line.location_id === place.location_id);
            const placeCounted = placeLines.filter((line) => line.counted_qty !== null).length;
            return (
              <button
                type="button"
                key={place.location_id}
                className={`stocktake__place-item${place.location_id === selectedLocation ? ' is-active' : ''}${place.status === 'completed' ? ' is-completed' : ''}`}
                onClick={() => selectPlace(place)}
                aria-current={place.location_id === selectedLocation ? 'true' : undefined}
              >
                <strong className="stocktake__place-name">{place.location_name ?? place.location_id}</strong>
                <small className="stocktake__place-progress">{placeCounted}/{placeLines.length}</small>
                <strong className={`stocktake__place-status stocktake__place-status--${place.status}`}>{t(`stocktakes.placeStatus.${place.status}`)}</strong>
              </button>
            );
          })}
        </div>
      </section>
      <div className="stocktake__place">
        <LocationPicker label={t('stocktakes.currentPlace')} value={selectedLocation} onChange={setLocationId} allowOutside={false} scanTarget onComponentScan={(componentId) => {
          void getComponent(componentId).then((picked) => {
            setComponent(picked);
            setQuantity(session.lines.find((line) => line.component_id === picked.id && line.location_id === selectedLocation)?.counted_qty ?? '');
            setMeasuring(false);
          });
        }} />
        {open && currentPlace && <button type="button" onClick={() => finishPlace.mutate({ place: currentPlace.location_id, complete: currentPlace.status !== 'completed' })}>{t(currentPlace.status === 'completed' ? 'stocktakes.reopenPlace' : 'stocktakes.completePlace')}</button>}
      </div>

      {open && selectedLocation && <SerialCounter session={session} locationId={selectedLocation} onSaved={refresh} onComponentScanned={(picked) => {
        setComponent(picked);
        setQuantity(session.lines.find((line) => line.component_id === picked.id && line.location_id === selectedLocation)?.counted_qty ?? '');
        setMeasuring(false);
      }} />}

      {open && selectedLocation && (
        <form className="stocktake__add" onSubmit={(event) => { event.preventDefault(); if (component && quantity) { count.mutate({ component_id: component.id, location_id: selectedLocation, counted_qty: quantity }); setComponent(null); setQuantity(''); } }}>
          <ComponentPicker label={t('stocktakes.component')} value={component} onChange={(next) => { setComponent(next); setMeasuring(false); setQuantity(next ? (session.lines.find((line) => line.component_id === next.id && line.location_id === selectedLocation)?.counted_qty ?? '') : ''); }} />
          <input type="number" min="0" step="any" inputMode="decimal" value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder={t('stocktakes.quantity')} />
          <button type="submit" disabled={!component || !quantity || component.track_serials}>{t('stocktakes.save')}</button>
          {component && !component.track_serials && selectedComponent.data?.resolved_measurement_profile && <button type="button" title={t('stocktakes.measure.title')} aria-label={t('stocktakes.measure.title')} onClick={() => setMeasuring((value) => !value)}><ScaleIcon /></button>}
        </form>
      )}
      {open && selectedLocation && component && !component.track_serials && measuring && (
        <MeasureCount sessionId={id} componentId={component.id} locationId={selectedLocation} expectedQty={session.lines.find((line) => line.component_id === component.id && line.location_id === selectedLocation)?.expected_qty ?? '0'} onSaved={(counted) => { setQuantity(counted); refresh(); }} />
      )}

      <div className="tabs">
        {(['pending', 'different', 'all'] as const).map((key) => <button type="button" key={key} className={filter === key ? 'tabs__tab is-active' : 'tabs__tab'} onClick={() => setFilter(key)}>{t(`stocktakes.filter.${key}`)}</button>)}
      </div>
      <div className="stocktake__lines">
        {lines.map((line) => <CountLine key={line.id} line={line} open={open} sessionId={id} onSaved={refresh} onOpenComponent={() => navigate(`/components/${line.component_id}`)} onCount={(value) => count.mutate({ component_id: line.component_id, location_id: line.location_id, counted_qty: value })} />)}
      </div>
      {open && <footer className="stocktake__actions"><button type="button" className="danger" onClick={() => cancel.mutate()}>{t('stocktakes.cancel')}</button><button type="button" className="primary" onClick={() => { const changed = session.lines.some((line) => line.changed_during_count); if (!changed || window.confirm(t('stocktakes.changedConfirm'))) post.mutate(); }}>{t('stocktakes.post')}</button></footer>}
    </section>
  );
}

function CountLine({ line, open, sessionId, onSaved, onOpenComponent, onCount }: { line: StocktakeLine; open: boolean; sessionId: string; onSaved: () => void; onOpenComponent: () => void; onCount: (value: string) => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(line.counted_qty ?? '');
  const [measuring, setMeasuring] = useState(Boolean(line.measurement));
  const component = useQuery({ queryKey: ['components', line.component_id], queryFn: () => getComponent(line.component_id), enabled: open });
  const canMeasure = Boolean(component.data?.resolved_measurement_profile);
  return <article className={line.counted_qty !== null && line.counted_qty !== line.expected_qty ? 'stocktake-line is-different' : 'stocktake-line'}>
    <div><strong><button type="button" className="component-form__scope" onClick={onOpenComponent}>{line.component_code}</button> · {line.component_name}</strong><small>{line.location_name}</small>{line.measurement && <small>{t('stocktakes.measure.recorded', { value: num(line.measurement.measured_value), unit: line.measurement.unit })}</small>}</div>
    <span>{t('stocktakes.expected')}: {num(line.expected_qty)}{line.changed_during_count && <small className="error"> · {t('stocktakes.changed')}</small>}</span>
    {line.track_serials ? <span>{t('stocktakes.serialCount')}: {line.counted_qty === null ? '—' : num(line.counted_qty)}</span> : <><form onSubmit={(event) => { event.preventDefault(); onCount(value); }}><input disabled={!open} type="number" min="0" step="any" value={value} onChange={(event) => setValue(event.target.value)} /><button disabled={!open || !value}>{t('stocktakes.save')}</button>{open && <button type="button" onClick={() => onCount(line.expected_qty)}>{t('stocktakes.matches')}</button>}{open && <button type="button" onClick={() => onCount('0')}>{t('stocktakes.empty')}</button>}{open && canMeasure && <button type="button" title={t('stocktakes.measure.title')} aria-label={t('stocktakes.measure.title')} onClick={() => setMeasuring((current) => !current)}><ScaleIcon /></button>}</form>{open && measuring && canMeasure && <MeasureCount sessionId={sessionId} componentId={line.component_id} locationId={line.location_id} expectedQty={line.expected_qty} onSaved={() => onSaved()} />}</>}
  </article>;
}

function MeasureCount({ sessionId, componentId, locationId, expectedQty, onSaved }: { sessionId: string; componentId: string; locationId: string; expectedQty: string; onSaved: (counted: string) => void }) {
  const { t } = useTranslation();
  const component = useQuery({ queryKey: ['components', componentId], queryFn: () => getComponent(componentId) });
  const profile = component.data?.resolved_measurement_profile;
  const [method, setMethod] = useState<'mass' | 'length'>('mass');
  const [value, setValue] = useState('');
  const [includeCarrier, setIncludeCarrier] = useState(true);
  const [result, setResult] = useState<string | null>(null);
  const measure = useMutation({
    mutationFn: () => measureStocktake(sessionId, { component_id: componentId, location_id: locationId, method, measured_value: value, include_carrier: includeCarrier }),
    onSuccess: (line) => { setValue(''); setResult(line.counted_qty); onSaved(line.counted_qty ?? ''); },
  });
  if (!profile) return null;
  const canUseLength = profile.mode === 'tape';
  const measured = Number(value.replace(',', '.'));
  const sampleWeight = Number(profile.sample_weight_g);
  const carrierWeight = Number(profile.carrier_weight_g);
  const estimated = !value.trim() || !Number.isFinite(measured) || measured < 0 || !Number.isFinite(sampleWeight) || sampleWeight <= 0
    ? null
    : method === 'mass'
      ? Math.round((measured - (includeCarrier ? carrierWeight : 0)) * profile.sample_quantity / sampleWeight)
      : profile.tape_length_10_cells_mm && Number(profile.tape_length_10_cells_mm) > 0
        ? Math.round(measured * 10 / Number(profile.tape_length_10_cells_mm))
        : null;
  const preview = estimated !== null && Number.isFinite(estimated) ? estimated : null;
  const canApply = preview !== null && preview >= 0;
  return <form className="stocktake__add stocktake__measure" onSubmit={(event) => { event.preventDefault(); if (value) measure.mutate(); }}>
    <strong>{t('stocktakes.measure.title')}</strong>
    <div className="stocktake__measure-controls">
      {canUseLength && <select value={method} onChange={(event) => setMethod(event.target.value as 'mass' | 'length')}><option value="mass">{t('stocktakes.measure.mass')}</option><option value="length">{t('stocktakes.measure.length')}</option></select>}
      <input type="number" min="0" step="any" inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value.replace(',', '.'))} placeholder={t(method === 'mass' ? 'stocktakes.measure.massPlaceholder' : 'stocktakes.measure.lengthPlaceholder')} />
      {method === 'mass' && <label className="check"><input type="checkbox" checked={includeCarrier} onChange={(event) => setIncludeCarrier(event.target.checked)} />{t('stocktakes.measure.includeCarrier')}</label>}
      <button disabled={!canApply || measure.isPending}>{preview === null ? t('stocktakes.measure.apply') : t('stocktakes.measure.applyPreview', { value: num(preview) })}</button>
    </div>
    {measure.isError && <span className="error">{t('stocktakes.measure.error')}</span>}
    {result !== null && <span className="hint">{component.data?.internal_code}: {t('stocktakes.expected')} {num(expectedQty)} → {num(result)}</span>}
  </form>;
}

function SerialCounter({ session, locationId, onSaved, onComponentScanned }: { session: Stocktake; locationId: string; onSaved: () => void; onComponentScanned: (component: ComponentBrief) => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [unknown, setUnknown] = useState(false);
  const [component, setComponent] = useState<ComponentBrief | null>(null);
  const observe = useMutation({ mutationFn: async (raw: string) => {
    const found = await resolveScan(raw);
    if (!found.resolved || !found.match) throw new Error('notSerial');
    if (found.match.kind === 'component') {
      onComponentScanned(await getComponent(found.match.id));
      return null;
    }
    if (found.match.kind !== 'serial') throw new Error('notSerial');
    return observeStocktakeSerial(session.id, { serial_unit_id: found.match.id, location_id: locationId });
  }, onSuccess: (saved) => { setCode(''); setMessage(saved ? 'saved' : null); setUnknown(false); setComponent(null); if (saved) onSaved(); }, onError: () => { setMessage('notSerial'); setUnknown(true); } });
  const register = useMutation({ mutationFn: () => observeStocktakeSerial(session.id, { component_id: component?.id, serial: code, location_id: locationId }), onSuccess: () => { setCode(''); setMessage('saved'); setUnknown(false); setComponent(null); onSaved(); } });
  return <div><form className="stocktake__serial" onSubmit={(event) => { event.preventDefault(); if (code) observe.mutate(code); }}><strong>{t('stocktakes.scanSerial')}</strong><input value={code} onChange={(event) => { setCode(event.target.value); setUnknown(false); }} placeholder="S-…" autoComplete="off" /><button disabled={!code || observe.isPending}>{t('stocktakes.find')}</button>{message && <span className={message === 'saved' ? 'hint' : 'error'}>{t(`stocktakes.${message}`)}</span>}</form>{unknown && <div className="stocktake__unknown"><p>{t('stocktakes.registerUnknown')}</p><ComponentPicker label={t('stocktakes.component')} value={component} onChange={setComponent} /><button type="button" disabled={!component || register.isPending} onClick={() => register.mutate()}>{t('stocktakes.register')}</button></div>}</div>;
}
